import { CommonSettingKey, type UserSetting } from '@gov-nx/api';
import type { LocalizationLanguage } from '@gov-nx/core/app';
import type { Optional } from '@gov-nx/core/types';
import { isArray, prop } from '@gov-nx/core/types';
import { usePersistedStore } from '../constants';
import type { UserState } from './store.types';
import {
	isFavoritesSetting,
	isLanguageSetting,
	isRecentVisitedSetting,
	isSearchTermsSetting,
	sanitizeRecentVisited,
	sanitizeSearchTerms,
} from './utils';
import type {
	SearchAllRecentVisited,
	SearchAllSearchTerms,
} from '@gov-nx/module/page';

const getSettingValue = <TSetting extends UserSetting>(
	settings: UserSetting[],
	guard: (setting: UserSetting) => setting is TSetting
): TSetting['value'] | undefined => settings.find(guard)?.value;

const initialState = {
	biometricAllowed: false,
	settings: [],
};

const useStore = <T>(selector: (state: UserState) => T) =>
	usePersistedStore(
		(set) => {
			const setUserSetting = (setting: UserSetting) =>
				set((state: UserState) => ({
					settings: [
						...state.settings.filter((i) => i.key !== setting.key),
						setting,
					],
				}));

			return {
				...initialState,

				setUserSettingFavorites: (favorites) =>
					setUserSetting({ key: CommonSettingKey.Favorites, value: favorites }),
				setUserSettingSearchTerms: (searchTerms) =>
					setUserSetting({
						key: CommonSettingKey.SearchTerms,
						value: searchTerms,
					}),
				setUserSettingRecentVisited: (recentVisited) =>
					setUserSetting({
						key: CommonSettingKey.RecentVisited,
						value: recentVisited,
					}),
				setUserSettingLanguage: (language) =>
					setUserSetting({
						key: CommonSettingKey.Language,
						value: language,
					}),
				setBiometricStatus: (biometricAllowed: boolean) =>
					set({ biometricAllowed }),
				userSubjectSettingsReset: () =>
					set((state: UserState) => {
						const language = getSettingValue(state.settings, isLanguageSetting);

						return {
							biometricAllowed: state.biometricAllowed,
							settings: language
								? [{ key: CommonSettingKey.Language, value: language }]
								: [],
						};
					}),
				userStoreReset: () => set(initialState),
			};
		},
		'user',
		selector
	);

export const useUserStore = <T extends keyof UserState>(
	property: T
): UserState[T] => useStore(prop(property));

export const useUserStoreFavorites = (): string[] => {
	const settings = useUserStore('settings');
	const foundValue = getSettingValue(settings, isFavoritesSetting);

	return isArray(foundValue) ? foundValue : [];
};

export const useUserStoreLanguage = (): Optional<LocalizationLanguage> => {
	const settings = useUserStore('settings');

	return getSettingValue(settings, isLanguageSetting);
};

export const useUserStoreSearchTerms = (): SearchAllSearchTerms[] => {
	const settings = useUserStore('settings');
	const foundValue = getSettingValue(settings, isSearchTermsSetting);

	return sanitizeSearchTerms(foundValue);
};

export const useUserStoreRecentVisited = (): SearchAllRecentVisited[] => {
	const settings = useUserStore('settings');
	const foundValue = getSettingValue(settings, isRecentVisitedSetting);

	return sanitizeRecentVisited(foundValue);
};
